import { Component, ElementRef, QueryList, ViewChildren, ViewChild, ViewContainerRef } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { ApiService } from "../../../../services/api.service";
import { ToasterService } from "../../../../services/toaster.service";
import { LoadingService } from "../../../../services/loading.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Vendors, VendorServiceMapping } from "../../../../models/vendor-master.interface";
import { AutocompleteDropdownComponent } from "../../../autocomplete-dropdown/autocomplete-dropdown.component";
import { BackButtonComponent } from "../../../back-button/back-button.component";
import { NgClass, NgForOf, NgIf } from "@angular/common";
import { SharedModule } from "../../../../modules/shared/shared.module";
import { SvgIconComponent } from "../../../svg-icon/svg-icon.component";
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { CustomAccordionComponent } from "../../../custom-accordion/custom-accordion.component";
import { PageCloseButtonComponent } from "../../../common/close-button/page-close-button.component";
import { ContactInformation } from "../../../../models/masters/contact-information.interface";
import { act } from "@ngrx/effects";
import { MultiSelectComponent } from "../../../select/multi-select.component";
import { DatePickerComponent } from "../../../common/date-picker/date-picker.component";
import { SectionHeaderComponent } from "../../../common/section-header/section-header.component";
import { HorizontalTabsComponent } from "../../../horizontal-tabs/horizontal-tabs.component";
import { FileUploadComponent } from "../../../file-upload/file-upload.component";
import { CONTACT_ACTIONS, ContactActions, ContactService } from "../../../../services/masters/contact.service";
import { ModalComponent } from "../../../modal/modal.component";
import { ContactDialogComponent } from "../../contact-information/contact-dialog/contact-dialog.component";
import { VendorContactComponent } from "../../contact-information/vendor-contact/vendor-contact.component";
import { InfoCardBannerComponent } from "../../../info-card-banner/info-card-banner.component";
import { FileUploadDTO } from "../../../../models/customer-agreement.interface";
import { FileSource, FileUploadService, isFileModified } from "../../../../services/file-upload.service";
import { NoDataComponent } from "../../../no-data/no-data.component";
import { CustomValidators } from "../../../../validators/custom-validators";
import { PAN_NUMBER_REGEX } from "../../../../constants/app-constants";
import { FileEntity } from "../../../../models/common-model.interface";

@Component({
  selector: 'app-manage-vendor',
  standalone: true,
  imports: [
    SharedModule,
    AutocompleteDropdownComponent,
    BackButtonComponent,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    SvgIconComponent,
    NgClass,
    MatExpansionModule,
    MatExpansionPanel,
    CustomAccordionComponent,
    PageCloseButtonComponent,
    MultiSelectComponent,
    DatePickerComponent,
    SectionHeaderComponent,
    HorizontalTabsComponent,
    FileUploadComponent,
    InfoCardBannerComponent,
    NoDataComponent
],
  templateUrl: './manage-vendor.component.html',
  styleUrl: './manage-vendor.component.scss',
})
export class ManageVendorComponent {
  readOnlyMode = true;
  editMode = false;
  masterForm: FormGroup;
  vendorId: string = '';
  serviceList: VendorServiceMapping[] = [];
  contactList: ContactInformation[] = [];
  fileList: FileEntity[]= [];
  existingFiles: FileUploadDTO[] = [];
  contactsLoaded = false;
  servicesLoaded = false;
  vendorData: Vendors | null = null;
  contactActions: { icon: string; name: string; class: string }[] = [];

  selectedService: any = {};
  deletedServiceIds: Set<String> = new Set();
  addedServicesSet: Set<String> = new Set();
  serviceSearchText: string = '';
  disableAddButton: boolean = true;

  vendorScetionId: string = 'vendorSection';
  selectedTab: string = this.vendorScetionId;
  private sectionMap: Map<string, any> = new Map();

  tabList = [
    { id: this.vendorScetionId, label: 'Vendor Information' },
    { id: 'contactSection', label: 'Contact Information' },
    { id: 'serviceSection', label: 'Manage Services' },
  ];

  vendorStatusOptions = [
    { name: 'HUF', value: 'HUF'},
    { name: 'Company', value: 'COMPANY'},
    { name: 'Individual', value: 'INDIVIDUAL'},
    { name: 'Partnership Firm', value: 'PARTNERSHIP_FIRM'},
    { name: 'Others', value: 'OTHER'},
  ];

  allowedFileTypes: string[] = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "text/pdf"
  ];

  @ViewChildren('formField') formFields!: QueryList<ElementRef>;
  @ViewChild('vendorSection') vendorSection: ElementRef | undefined;
  @ViewChild('contactSection') contactSection: ElementRef | undefined;
  @ViewChild('serviceSection') serviceSection: ElementRef | undefined;


  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private toasterService: ToasterService,
    private loadingService: LoadingService,
    private router: Router,
    private route: ActivatedRoute,
    private viewContainerRef: ViewContainerRef,
    private contactService: ContactService,
    private fileUploadService: FileUploadService
  ) {
    this.masterForm = this.initForm();
  }

  ngOnInit(): void {
    this.vendorId = this.route.snapshot.paramMap.get('vendorId') || '';
    console.log(this.vendorId);
    this.setupComponentMode();
    if (this.vendorId) {
      this.loadVendorData();
    }
  }

  async loadVendor() {
    await this.loadVendorData();
  }

  ngAfterViewInit() {
    if (this.vendorSection)
      this.sectionMap.set(this.vendorScetionId, this.vendorSection.nativeElement);
    if (this.contactSection)
      this.sectionMap.set('contactSection', this.contactSection.nativeElement);
     if (this.serviceSection)
       this.sectionMap.set('serviceSection', this.serviceSection.nativeElement);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): FormGroup {
    return this.fb.group({
      id: [null],
      code: [null, [Validators.required, Validators.maxLength(10)]],
      name: [null, Validators.required],
      active: [true],
      panNumber: [null, [Validators.required, CustomValidators.pattern(PAN_NUMBER_REGEX)]],
      agreementWorkOrderNo: [null],
      agreementStartDate: [null],
      agreementEndDate: [null],
      creditDays: [0],
      creditLimit: [0],
      vendorStatus: [null],
      otherStatusValue: [null, [Validators.required, Validators.maxLength(30)]],
      identityNo: [null, [Validators.required, Validators.maxLength(20)]]
    });
  }

  private setupComponentMode(): void {
    if (this.vendorId) {
      const path = this.route.snapshot.url[0].path;
      this.editMode = path === 'edit';
      this.readOnlyMode = !this.editMode;
      if (this.readOnlyMode) {
        this.masterForm.disable();
      }
    } else {
      this.readOnlyMode = false;
      this.editMode = false;
    }
    this.contactActions = this.contactService.getContactActions(this.readOnlyMode);
  }

  async selectTab(sectionId: string) {
    if (this.selectedTab === sectionId) return;

    this.selectedTab = sectionId;
    const section = this.sectionMap.get(sectionId);
    const container = document.querySelector('.full_page_container');
    if (sectionId === 'contactSection') {
      //await this.loadVendorContacts();
    } else if (sectionId === 'serviceSection') {
      //await this.loadVendorServices();
    }
    if (section && container) {
      const sectionTop =
        section.getBoundingClientRect().top -
        container.getBoundingClientRect().top;
      container.scrollTo({
        top: sectionTop + container.scrollTop,
        behavior: 'smooth',
      });
    }
  }

  handleContactAction(index: number, action: string) {
    if (action === 'edit') {
      this.createModalForContactConfiguration(CONTACT_ACTIONS.EDIT, index);
    } else if (action === 'view') {
      this.createModalForContactConfiguration(CONTACT_ACTIONS.VIEW, index);
    } else if (action === 'clone') {
      this.createModalForContactConfiguration(CONTACT_ACTIONS.CLONE, index);
    }
  }

  addContact() {
    this.createModalForContactConfiguration(CONTACT_ACTIONS.ADD);
  }

  private createModalForContactConfiguration(action: ContactActions, index?: number) {
    const modalComponentComponentRef = this.viewContainerRef.createComponent(ModalComponent);
    modalComponentComponentRef.instance.width = "90vw";
    modalComponentComponentRef.instance.height = "95vh";
    modalComponentComponentRef.instance.title = this.getModalTitle(action);
    modalComponentComponentRef.instance.scrollable = true;

    const componentRef = modalComponentComponentRef.instance.loadComponent(VendorContactComponent);
    componentRef.instance.action = action;
    componentRef.instance.contactInformation = index !== undefined ? this.contactList[index] : null;

    modalComponentComponentRef.instance.close.subscribe((res: any) => {
      if (res) {
        console.log("res: ", res);
        const isDefaultContact = res.defaultContact ?? false;
        if (action === CONTACT_ACTIONS.ADD) {
          this.contactList.push(res);
          index = this.contactList.length - 1;
        } else if (action === CONTACT_ACTIONS.EDIT && index !== undefined) {
          this.contactList[index] = res;
        } else if (action === CONTACT_ACTIONS.CLONE) {
          this.contactList.push(res);
          index = this.contactList.length - 1;
        } 
        this.onDefaultContactChange(isDefaultContact, index!);
      }
      modalComponentComponentRef.destroy();
    });
  }

  private onDefaultContactChange(state: boolean, i: number) {
    if (state) {
      this.contactList.forEach((contact, index) => {
        contact.defaultContact = index === i ? state : false;
      });
    }
  }

  private getModalTitle(action: ContactActions): string {
    switch (action) {
      case CONTACT_ACTIONS.ADD: return 'Add Contact';
      case CONTACT_ACTIONS.EDIT: return 'Edit Contact';
      case CONTACT_ACTIONS.VIEW: return 'View Contact';
      default: return 'Contact';
    }
  }

  async loadVendorData(): Promise<void> {
    this.loadingService.show();
    const res = await firstValueFrom(this.api.getVendorMaster(this.vendorId, true));
    this.vendorData = res.data as Vendors;
    await this.loadAndAssignUploadedFiles(this.vendorData);
    this.patchFormWithMasterData(this.vendorData);
    this.loadingService.hide();
  }

  async loadVendorContacts(): Promise<void> {
    if (this.contactsLoaded || !this.vendorId) return;
    this.loadingService.show();
    const res = await firstValueFrom(this.api.getVendorContacts(this.vendorId));
    this.contactList = res.data as ContactInformation[];
    this.contactsLoaded = true;
    this.loadingService.hide();
  }

  async loadVendorServices(): Promise<void> {
    if (this.servicesLoaded || !this.vendorId) return;
    this.loadingService.show();
    const res = await firstValueFrom(this.api.getVendorServices(this.vendorId));
    this.serviceList = res.data as VendorServiceMapping[];
    this.servicesLoaded = true;
    this.serviceList.forEach((vendorService) =>
      this.addedServicesSet.add(vendorService.serviceId)
    );
    this.loadingService.hide();
  }

  private patchFormWithMasterData(vendors: Vendors): void {
    this.masterForm.patchValue({
      ...vendors,
      vendorStatus: vendors.vendorStatus?.id
    });
    this.contactList = vendors.contactInformation ?? [];
    this.serviceList = vendors.selectedServices ?? [];
    this.contactList.forEach((contact, index) => contact.contactName = contact.contactName ?? 'Contact ' + (index + 1));
  }

  onVendorStatusChange(selectedVendorStatus: string) {
    if (selectedVendorStatus === 'OTHER') {
      this.masterForm.get('identityNo')?.disable();
      this.masterForm.get('otherStatusValue')?.enable();
    } else {
      this.masterForm.get('identityNo')?.enable();
      this.masterForm.get('otherStatusValue')?.disable();
    }
  }

  onServiceSelected(service: any) {
    this.selectedService = service;
    this.disableAddButton = false;
  }

  addSelectedService() {
    if (!this.addedServicesSet.has(this.selectedService.id)) {
      this.addedServicesSet.add(this.selectedService.id);
      this.deletedServiceIds.delete(this.selectedService.id);
      this.serviceList.push({
        serviceId: this.selectedService.id,
        serviceMetadata: { name: this.selectedService.name, active: this.selectedService.active },
        default: false,
      });
    }
    this.serviceSearchText = '';
    this.disableAddButton = true;
  }

  selectVendorAsDefault(index: number) {
    this.serviceList[index].default = !this.serviceList[index].default;
  }

  removeService(index: number) {
    const serviceId = this.serviceList[index].serviceId;
    this.addedServicesSet.delete(serviceId);
    if (this.editMode) {
      this.deletedServiceIds.add(serviceId);
    }
    this.serviceList.splice(index, 1);
  }

  async submitMasterForm(): Promise<void> {
    this.masterForm.markAllAsTouched();

    if (!this.masterForm.valid) {
      this.selectTab(this.vendorScetionId);
      this.scrollToFirstInvalidControl();
      return;
    } 

    const fileUploads = await this.uploadFiles();
    const masterData: Vendors = {
        ...this.masterForm.value,
        active: true,
        contactInformation: this.contactList,
        selectedServices: this.serviceList,
        deletedServiceIds: [...this.deletedServiceIds],
        fileIds: fileUploads
    };
  
    this.loadingService.show();
    
    const action$ = this.editMode
      ? this.api.updateVendorMaster(masterData, this.vendorId)
      : this.api.addVendorMaster(masterData);

    action$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        const message = this.editMode
          ? `Vendor ${masterData.name} has been updated!`
          : `Vendor ${masterData.name} has been added!`;
        this.toasterService.success(message);
        this.loadingService.hide();
        this.goBack();
      },
      error: (err) => {
        this.toasterService.error(err.error.errorDesc || 'An error occurred');
        console.error('Error submitting form:', err);
        this.loadingService.hide();
      },
    });
  }

  async uploadFiles(): Promise<string[]> {
    await this.uploadContactFilesInParallel();
  
    const currentFiles = this.fileList ?? [];
    const previouslyUploaded = this.existingFiles ?? [];

    const finalFileIds: string[] = [];
    const uploadTasks: Promise<string>[] = [];

    // Track which uploaded files are retained (matched by name & MIME type)
    const retained = new Set<string>();
    const prevMap = new Map(previouslyUploaded.map(f => [f.id, f]));
    
    for (const fileEntity of currentFiles) {
      if (!fileEntity.id) {
        uploadTasks.push(this.fileUploadService.uploadFile(fileEntity.file, FileSource.VENDOR));
        continue;
      }
      const match = prevMap.get(fileEntity.id);
      if (match) {
        finalFileIds.push(match.id);
        retained.add(match.id);
      } else {
        uploadTasks.push(this.fileUploadService.uploadFile(fileEntity.file, FileSource.VENDOR));
      }
    }

    const deletedFiles = previouslyUploaded.filter(prev => !retained.has(prev.id));

    const uploadedFileIds = await Promise.all(uploadTasks);

    return [...finalFileIds, ...uploadedFileIds];
  }

  async uploadContactFilesInParallel(): Promise<void> {
    const uploadPromises: Promise<void>[] = [];
  
    for (const contact of this.contactList) {
      const { tdsMetadata, msmeInfo, lutInfo } = contact;  
      // TDS File -  Upload if not changed
      if (tdsMetadata && tdsMetadata?.file && !tdsMetadata?.file?.id) {
        uploadPromises.push(
          this.fileUploadService.uploadFile(tdsMetadata.file.file, FileSource.CONTACT).then(fileId => {
            tdsMetadata.fileId = fileId;
          })
        );
      }
  
      // MSME File - Upload if not changed
      if (msmeInfo && msmeInfo?.file && !msmeInfo?.file?.id) {
        uploadPromises.push(
          this.fileUploadService.uploadFile(msmeInfo.file.file, FileSource.CONTACT).then(fileId => {
            msmeInfo.fileId = fileId;
          })
        );
      }
  
      // LUT File - Upload if not changed
      if (lutInfo && lutInfo?.file && !lutInfo?.file?.id) {
        uploadPromises.push(
          this.fileUploadService.uploadFile(lutInfo.file.file, FileSource.CONTACT).then(fileId => {
            lutInfo.fileId = fileId;
          })
        );
      }

      await this.uploadBankAccountFilesInParallel(contact);
    }
  
    // Run all uploads in parallel
    await Promise.all(uploadPromises);
  }

  async uploadBankAccountFilesInParallel(contactInfo: ContactInformation): Promise<void> {
    if (!contactInfo.bankAccounts?.length) return;

    const uploadPromises: Promise<void>[] = [];
    for (const account of contactInfo.bankAccounts) {
      console.log(account);
      if (account.file && !account?.file.id) {
        uploadPromises.push(
          this.fileUploadService.uploadFile(account.file.file, FileSource.CONTACT).then(fileId => {
            account.fileId = fileId;
          })
        );
      }
    }
    await Promise.all(uploadPromises);
  }

  private async loadAndAssignUploadedFiles(vendorData: Vendors): Promise<void> {
    const fileIds: string[] = [];
  
    // Collect file ids from vendor and each contact
    if (vendorData.fileIds?.length) {
      fileIds.push(...vendorData.fileIds);
    }
  
    for (const contact of vendorData.contactInformation ?? []) {
      const { tdsMetadata, msmeInfo, lutInfo } = contact;
  
      if (tdsMetadata?.fileId) fileIds.push(tdsMetadata.fileId);
      if (msmeInfo?.fileId) fileIds.push(msmeInfo.fileId);
      if (lutInfo?.fileId) fileIds.push(lutInfo.fileId);

      contact.bankAccounts?.map(account => account?.fileId).filter((id): id is string => !!id).forEach((id) =>  fileIds.push(id));
    }
  
    if (fileIds.length === 0) return;
  
    // Get all the files
    const fileMetaResponse = await firstValueFrom(this.api.getFileMetadata(fileIds));
    const fileMetadata: FileUploadDTO[] = fileMetaResponse.data;

    const fileMap = new Map<string, FileUploadDTO>(
      fileMetadata.map(f => [f.id, f])
    );

    this.existingFiles = vendorData.fileIds?.map(id => fileMap.get(id)!).filter(Boolean) ?? [];
    this.fileList = this.fileUploadService.convertToFileFromFileUploadDto(this.existingFiles);

    for (const contact of vendorData.contactInformation ?? []) {
      const { tdsMetadata, msmeInfo, lutInfo } = contact;

      if (tdsMetadata?.fileId) {
        const uploaded = fileMap.get(tdsMetadata.fileId);
        if (uploaded) {
          tdsMetadata.uploadedFile = uploaded;
          tdsMetadata.file = this.fileUploadService.convertToFileFromDTO(uploaded);
        }
      }
  
      if (msmeInfo?.fileId) {
        const uploaded = fileMap.get(msmeInfo.fileId);
        if (uploaded) {
          msmeInfo.uploadedFile = uploaded;
          msmeInfo.file = this.fileUploadService.convertToFileFromDTO(uploaded);
        }
      }
  
      if (lutInfo?.fileId) {
        const uploaded = fileMap.get(lutInfo.fileId);
        if (uploaded) {
          lutInfo.uploadedFile = uploaded;
          lutInfo.file = this.fileUploadService.convertToFileFromDTO(uploaded);
        }
      }

      for (const account of contact.bankAccounts ?? []) {
        if (account?.fileId) {
          const uploaded = fileMap.get(account.fileId);
          if (uploaded) {
            account.uploadedFile = uploaded;
            account.file = this.fileUploadService.convertToFileFromDTO(uploaded);
          }
        }
      }
    }
  }
  

  scrollToFirstInvalidControl(): void {
    const firstInvalidControl = this.formFields.find((element) => {
      const nativeElement = element.nativeElement as HTMLElement;
      return nativeElement.classList.contains('ng-invalid');
    });

    if (firstInvalidControl) {
      firstInvalidControl.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      (firstInvalidControl.nativeElement as HTMLElement).focus();
    }
  }

  goBack(): void {
    this.router.navigateByUrl('/manage-masters/vendors');
  }

  getFormattedContactSummary(i: number): string[] {
    return Array.of(this.contactService.getFormattedContactSummary(this.contactList[i]));
  }

  getContactBadges(i: number): { label: string; class: string }[] {
    return this.contactService.getContactBadges(this.contactList[i]);
  }
}
